import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ManagementColumns from '../../components/management-columns/ManagementColumns';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import OpenSVG from '../../components/svgs/Open.svg';
import RsgSiteModal from './RsgSiteModal';
import RsgTankModal from './RsgTankModal';
import RsgCanisterModal from './RsgCanisterModal';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { LABEL_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';
import {
  getStorageCanisterCount,
  getStorageCanisters,
  getStorageSites,
  getStorageTankInventoryCount,
  getStorageTanks,
} from '../../api/storageItemsApi';
import { CanisterContentsAutoFill, PagedResponse, StorageCanister, StorageSite, StorageTank } from '../../types/interfaces';
import { ManagementColumnFilterTypes } from '../../types/enums';
import './locationManagement.scss';

interface StorageSiteOption extends StorageSite {
  storageTankCount?: number;
}

interface StorageTankOption extends StorageTank {
  storageCanisterCount?: number;
}

interface StorageCanisterOption extends StorageCanister {
  storageCount?: number;
}

const LocationManagement: FC = () => {
  const navigate = useNavigate();

  const [totalSites, setTotalSites] = useState<number>();
  const [totalTanks, setTotalTanks] = useState<number>();
  const [totalCanisters, setTotalCanisters] = useState<number>();
  const [showFilter, setShowFilter] = useState<FilterOption<ManagementColumnFilterTypes>>({
    name: LABEL_CONSTANTS.ACTIVE_INACTIVE,
    value: ManagementColumnFilterTypes.BOTH,
  });

  const [sites, setSites] = useState<StorageSiteOption[]>([]);
  const [selectedSite, setSelectedSite] = useState<StorageSiteOption>();
  const [tanks, setTanks] = useState<StorageTankOption[]>([]);
  const [selectedTank, setSelectedTank] = useState<StorageTankOption>();
  const [canisters, setCanisters] = useState<StorageCanister[]>([]);
  const [selectedCanister, setSelectedCanister] = useState<StorageCanister>();

  const [newSiteId, setNewSiteId] = useState<number>();
  const [newTankId, setNewTankId] = useState<number>();
  const [newCanisterId, setNewCanisterId] = useState<number>();

  const [siteRefresh, setSiteRefresh] = useState<number>(0);
  const [tankRefresh, setTankRefresh] = useState<number>(0);
  const [canisterRefresh, setCanisterRefresh] = useState<number>(0);

  const [tankInventoryCount, setTankInventoryCount] = useState<number>();
  const [canisterCount, setCanisterCount] = useState<number>();

  const [offsetSites, setOffsetSites] = useState<number>(0);
  const [offsetTanks, setOffsetTanks] = useState<number>(0);
  const [offsetCanisters, setOffsetCanisters] = useState<number>(0);

  const hasAllSites: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const hasAllTanks: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const hasAllCanisters: React.MutableRefObject<boolean> = useRef<boolean>(false);

  const isLoadingAdditionalSites: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingAdditionalTanks: React.MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingAdditionalCanisters: React.MutableRefObject<boolean> = useRef<boolean>(false);

  const callNumberSites: React.MutableRefObject<number> = useRef<number>(0);
  const callNumberTanks: React.MutableRefObject<number> = useRef<number>(0);
  const callNumberCanisters: React.MutableRefObject<number> = useRef<number>(0);

  const [loadingSites, setLoadingSites] = useState<boolean>(false);
  const [loadingTanks, setLoadingTanks] = useState<boolean>(false);
  const [loadingCanisters, setLoadingCanisters] = useState<boolean>(false);

  const limit: number = 10;

  useEffect(() => {
    getTotals();
  }, [newSiteId, newTankId, newCanisterId]);

  const getTotals = async () => {
    try {
      const [{ data: sitesResponse }, { data: tanksResponse }, { data: canistersResponse }] = await Promise.all([
        getStorageSites({ limit: 1 }),
        getStorageTanks({ limit: 1 }),
        getStorageCanisters({ limit: 1 }),
      ]);
      setTotalSites((sitesResponse as PagedResponse<StorageSite>).total);
      setTotalTanks((tanksResponse as PagedResponse<StorageTank>).total);
      setTotalCanisters((canistersResponse as PagedResponse<StorageCanister>).total);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    getSites();
  }, [offsetSites]);

  useEffect(() => {
    getTanks();
  }, [offsetTanks]);

  useEffect(() => {
    getCanisters();
  }, [offsetCanisters]);

  const getSites = async () => {
    if (hasAllSites.current) {
      return;
    }
    setLoadingSites(true);
    callNumberSites.current++;
    const callNum = callNumberSites.current;
    try {
      const query = { filter: showFilter.value, limit: limit, offset: offsetSites, sort: 'Name' };
      const { data: storageSites } = await getStorageSites(query);
      if (callNum === callNumberSites.current) {
        const sitesList = (storageSites as PagedResponse<StorageSite>).result;
        const newSites = await Promise.all(
          sitesList.map(async (s: StorageSite) => {
            try {
              const { data: tResponse } = await getStorageTanks({ filter: 'storageSiteId eq ' + s.storageSiteId, limit: 1 });
              return { ...s, storageTankCount: (tResponse as PagedResponse<StorageTank>).total };
            } catch {
              return s;
            }
          }),
        );
        if (callNum === callNumberSites.current) {
          setSites(offsetSites > 0 ? [...sites, ...newSites] : newSites);
          hasAllSites.current = newSites.length === 0;
          setLoadingSites(false);
        }
      }
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const getTanks = async () => {
    if (hasAllTanks.current) {
      return;
    }
    setLoadingTanks(true);
    callNumberTanks.current++;
    const callNum = callNumberTanks.current;
    try {
      if (selectedSite) {
        const query = {
          filter: showFilter.value + 'storageSiteId eq ' + selectedSite!.storageSiteId.toString(),
          limit: limit,
          offset: offsetTanks,
          sort: 'Name',
        };
        const { data: storageTanks } = await getStorageTanks(query);
        if (callNum === callNumberTanks.current) {
          const tanksList = (storageTanks as PagedResponse<StorageTank>).result;
          const newTanks: StorageTankOption[] = await Promise.all(
            tanksList.map(async (t: StorageTank) => {
              try {
                const { data: cResponse } = await getStorageCanisters({
                  filter: 'storageTankId eq ' + t.storageTankId,
                  limit: 1,
                });
                return { ...t, storageCanisterCount: (cResponse as PagedResponse<StorageCanister>).total };
              } catch {
                return t;
              }
            }),
          );

          if (callNum === callNumberTanks.current) {
            setTanks(offsetTanks > 0 ? [...tanks, ...newTanks] : newTanks);
            hasAllTanks.current = newTanks.length === 0;
            setLoadingTanks(false);
          }
        }
      } else {
        setTanks([]);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setSelectedTank(undefined);
      setSelectedCanister(undefined);
      setCanisters([]);
    }
  };

  const getCanisters = async () => {
    if (hasAllCanisters.current) {
      return;
    }
    setLoadingCanisters(true);
    callNumberCanisters.current++;
    const callNum = callNumberCanisters.current;
    try {
      if (selectedTank) {
        const query = {
          filter: showFilter.value + 'storageTankId eq ' + selectedTank!.storageTankId.toString(),
          limit: limit,
          offset: offsetCanisters,
          sort: 'Name',
        };
        const { data: storageCanisters } = await getStorageCanisters(query);
        if (callNum === callNumberCanisters.current) {
          const canisterList = (storageCanisters as PagedResponse<StorageCanister>).result;
          const newCanisters: StorageCanisterOption[] = await Promise.all(
            canisterList.map(async (t: StorageCanister) => {
              try {
                const { data: cResponse } = await getStorageCanisterCount(t.storageCanisterId);
                return { ...t, storageCount: cResponse };
              } catch {
                return t;
              }
            }),
          );

          setCanisters(offsetCanisters > 0 ? [...canisters, ...newCanisters] : newCanisters);
          hasAllCanisters.current = newCanisters.length === 0;
          setLoadingCanisters(false);
        }
      } else {
        setCanisters([]);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setSelectedCanister(undefined);
    }
  };

  useEffect(() => {
    hasAllSites.current = false;
    setSites([]);
    setSelectedSite(undefined);
    if (offsetSites === 0) {
      getSites();
    } else {
      setOffsetSites(0);
    }
  }, [showFilter, siteRefresh]);

  useEffect(() => {
    hasAllTanks.current = false;
    setTanks([]);
    setSelectedTank(undefined);
    if (offsetTanks === 0) {
      getTanks();
    } else {
      setOffsetTanks(0);
    }
  }, [selectedSite, showFilter, tankRefresh]);

  useEffect(() => {
    hasAllCanisters.current = false;
    setCanisters([]);
    setSelectedCanister(undefined);
    if (offsetCanisters === 0) {
      getCanisters();
    } else {
      setOffsetCanisters(0);
    }
  }, [selectedTank, showFilter, canisterRefresh]);

  useEffect(() => {
    const getCanisterCount = async (storageCanisterId: number) => {
      try {
        const { data: count } = await getStorageCanisterCount(storageCanisterId);
        setCanisterCount(count);
      } catch {
        setCanisterCount(undefined);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };
    if (selectedCanister?.storageCanisterId) {
      getCanisterCount(selectedCanister?.storageCanisterId);
    } else {
      setCanisterCount(undefined);
    }
  }, [selectedCanister]);

  useEffect(() => {
    const getTankInventoryCount = async (storageTankId: number) => {
      try {
        const { data: count } = await getStorageTankInventoryCount(storageTankId);
        setTankInventoryCount(count);
      } catch {
        setTankInventoryCount(undefined);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };

    if (selectedTank?.storageTankId) {
      getTankInventoryCount(selectedTank?.storageTankId);
    } else {
      setTankInventoryCount(undefined);
    }
  }, [selectedTank]);

  const loadMoreSites = () => {
    setOffsetSites(offsetSites + limit);
  };

  const loadMoreTanks = () => {
    setOffsetTanks(offsetTanks + limit);
  };

  const loadMoreCanisters = () => {
    setOffsetCanisters(offsetCanisters + limit);
  };

  useEffect(() => {
    isLoadingAdditionalSites.current = false;
  }, [sites]);

  useEffect(() => {
    const getSingleSite = async (id: number) => {
      const { data: storageSites } = await getStorageSites({ filter: `StorageSiteId eq ${id}` });
      if ((storageSites as StorageSite[]).length > 0) {
        const s = (storageSites as StorageSite[])[0];
        setSelectedSite(s);
      }
    };

    if (newSiteId) {
      getSingleSite(newSiteId);
    }
  }, [newSiteId]);

  useEffect(() => {
    isLoadingAdditionalTanks.current = false;
  }, [tanks]);

  useEffect(() => {
    const getSingleTank = async (id: number) => {
      const { data: storageTanks } = await getStorageTanks({ filter: `StorageTankId eq ${id}` });
      if ((storageTanks as StorageTank[]).length > 0) {
        const s = (storageTanks as StorageTank[])[0];
        setSelectedTank(s);

        setSites(
          sites.map((site: StorageSiteOption) => {
            if (site.storageSiteId === s.storageSiteId) {
              return { ...site, storageTankCount: (site.storageTankCount ?? 0) + 1 };
            }
            return site;
          }),
        );
      }
    };

    if (newTankId) {
      getSingleTank(newTankId);
    }
  }, [newTankId]);

  useEffect(() => {
    isLoadingAdditionalCanisters.current = false;
  }, [canisters]);

  useEffect(() => {
    const getSingleCanister = async (id: number) => {
      const { data: storageCanisters } = await getStorageCanisters({ filter: `StorageCanisterId eq ${id}` });
      if ((storageCanisters as StorageCanister[]).length > 0) {
        const s = (storageCanisters as StorageCanister[])[0];
        setSelectedCanister(s);

        setTanks(
          tanks.map((tank: StorageTankOption) => {
            if (tank.storageTankId === s.storageTankId) {
              return { ...tank, storageCanisterCount: (tank.storageCanisterCount ?? 0) + 1 };
            }
            return tank;
          }),
        );
      }
    };

    if (newCanisterId) {
      getSingleCanister(newCanisterId);
    }
  }, [newCanisterId]);

  const openCanisterContents = (canister: StorageCanister) => {
    if (selectedSite && selectedTank) {
      const autoFill: CanisterContentsAutoFill = {
        site: { value: selectedSite?.storageSiteId.toString(), name: selectedSite?.name },
        tank: { value: selectedTank?.storageTankId.toString(), name: selectedTank?.name },
        canister: { value: canister.storageCanisterId.toString(), name: canister.name },
      };
      navigate(ROUTE_PATHS.APP_INVENTORY_MANAGEMENT_CANISTER_CONTENTS, { state: autoFill });
    }
  };

  return (
    <div className="location-management">
      <ManagementColumns
        header={PAGE_HEADER_CONSTANTS.LOCATION_INFO}
        filter={showFilter}
        setFilter={setShowFilter}
        headerStats={[
          { value: totalSites, label: LABEL_CONSTANTS.SITES },
          { value: totalTanks, label: LABEL_CONSTANTS.TANKS },
          { value: totalCanisters, label: LABEL_CONSTANTS.CANISTERS },
        ]}
        infoStats={[
          { label: LABEL_CONSTANTS.TANK_INVENTORY_COUNT, value: tankInventoryCount },
          { label: LABEL_CONSTANTS.CANISTER_INVENTORY_COUNT, value: canisterCount },
        ]}
        columns={[
          {
            header: LABEL_CONSTANTS.SITES,
            items: sites,
            selectedItemId: selectedSite?.storageSiteId,
            getItemId: (item: StorageSite) => item.storageSiteId,
            getItemAddModal: (close: () => void) => (
              <RsgSiteModal
                mode="add"
                close={() => {
                  setSiteRefresh(p => p + 1);
                  close();
                }}
                setSelectedId={setNewSiteId}
              />
            ),
            addItemButton: { label: LABEL_CONSTANTS.SITE },
            getItemLabel: (site: StorageSiteOption) => (
              <>
                <span className={site.active ? '' : 'inactive'}>
                  {site.name}
                  {!site.active && <>&emsp;({LABEL_CONSTANTS.INACTIVE})</>}
                </span>
                {site.storageTankCount !== undefined && site.storageTankCount > 0 && (
                  <span className="item-count-chip">{site.storageTankCount}</span>
                )}
              </>
            ),
            itemSelect: site => setSelectedSite(site),
            getItemEditModal: (close: () => void, site: StorageSite) => (
              <RsgSiteModal
                mode="edit"
                site={site}
                close={() => {
                  setSiteRefresh(p => p + 1);
                  close();
                }}
              />
            ),
            scroll: () => {
              if (!isLoadingAdditionalSites.current) {
                isLoadingAdditionalSites.current = true;
                loadMoreSites();
              }
            },
            loading: loadingSites,
          },
          {
            header: LABEL_CONSTANTS.TANKS,
            items: tanks,
            selectedItemId: selectedTank?.storageTankId,
            getItemId: (item: StorageTank) => item.storageTankId,
            getItemAddModal: (close: () => void) => (
              <RsgTankModal
                mode="add"
                close={() => {
                  setTankRefresh(p => p + 1);
                  close();
                }}
                site={selectedSite!}
                setSelectedId={setNewTankId}
              />
            ),
            addItemButton: { label: LABEL_CONSTANTS.TANK, disabled: selectedSite === undefined },
            getItemLabel: (tank: StorageTankOption) => (
              <>
                <span className={tank.active ? '' : 'inactive'}>
                  {tank.name}
                  {!tank.active && <>&emsp;({LABEL_CONSTANTS.INACTIVE})</>}
                </span>
                {tank.storageCanisterCount !== undefined && tank.storageCanisterCount > 0 && (
                  <span className="item-count-chip">{tank.storageCanisterCount}</span>
                )}
              </>
            ),
            itemSelect: tank => setSelectedTank(tank),
            getItemEditModal: (close: () => void, tank: StorageTank) => (
              <RsgTankModal
                mode="edit"
                close={() => {
                  setTankRefresh(p => p + 1);
                  close();
                }}
                site={selectedSite!}
                tank={tank}
              />
            ),
            scroll: () => {
              if (!isLoadingAdditionalTanks.current) {
                isLoadingAdditionalTanks.current = true;
                loadMoreTanks();
              }
            },
            loading: loadingTanks && selectedSite !== undefined,
          },
          {
            header: LABEL_CONSTANTS.CANISTERS,
            items: canisters,
            selectedItemId: selectedCanister?.storageCanisterId,
            getItemId: (item: StorageCanister) => item.storageCanisterId,
            getItemAddModal: (close: () => void) => (
              <RsgCanisterModal
                mode="add"
                close={() => {
                  setCanisterRefresh(p => p + 1);
                  close();
                }}
                site={selectedSite!}
                tank={selectedTank!}
                setSelectedId={setNewCanisterId}
              />
            ),
            addItemButton: {
              label: LABEL_CONSTANTS.CANISTER,
              disabled: selectedSite === undefined || selectedTank === undefined,
            },
            getItemLabel: (canister: StorageCanisterOption) => (
              <>
                <span className={canister.active ? '' : 'inactive'}>
                  {canister.name}
                  {!canister.active && <>&emsp;({LABEL_CONSTANTS.INACTIVE})</>}
                </span>
                <span className="canister-contents-label">
                  {canister.storageCount !== undefined && canister.storageCount > 0 && (
                    <span className="item-count-chip">{canister.storageCount}</span>
                  )}
                  <button className="icon button" onClick={() => openCanisterContents(canister)}>
                    <OpenSVG />
                  </button>
                </span>
              </>
            ),
            itemSelect: canister => setSelectedCanister(canister),
            getItemEditModal: (close: () => void, canister: StorageCanister) => (
              <RsgCanisterModal
                mode="edit"
                close={() => {
                  setCanisterRefresh(p => p + 1);
                  close();
                }}
                site={selectedSite!}
                tank={selectedTank!}
                canister={canister}
              />
            ),
            scroll: () => {
              if (!isLoadingAdditionalCanisters.current) {
                isLoadingAdditionalCanisters.current = true;
                loadMoreCanisters();
              }
            },
            loading: loadingCanisters && selectedTank !== undefined,
          },
        ]}
      />
    </div>
  );
};

export default LocationManagement;
